import {
  useEffect,
  useState,
  useMemo,
  useCallback,
  FC,
  useLayoutEffect,
} from "react";

import { useReactiveVar } from "@apollo/client";
import { ThemeProvider } from "@emotion/react";
import { LoadingPage } from "@hl/base-components/lib/LoadingPage";
import { useAlert } from "@hl/base-components/lib/alert/useAlert";
import {
  Browser,
  PencilFill,
  LockFill,
  LockOpenFill,
  ArrowLeft as ArrowLeftIcon,
} from "@hl/base-components/lib/assets/icons.generated";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks";
import { useCheckGateAccessQuery } from "@hl/shared-features/lib/features/gate/queries/gate.graphql.generated";
import { AppSEO } from "@hl/shared-features/lib/features/layout/AppSEO";
import useBeforeunload from "@hl/shared-features/lib/hooks/useBeforeunload";
import usePageVisitedEvent from "@hl/shared-features/lib/hooks/usePageVisitedEvent";
import { usePrompt } from "@hl/shared-features/lib/hooks/usePrompt";
import { logError } from "@hl/shared-features/lib/services/logger";
import { $canShowPlaceholder } from "@lexical/text";
import {
  AppShell,
  Button,
  Card,
  Container,
  Grid,
  Group,
  Stack,
  Tabs,
  Text,
} from "@mantine/core";
import { EditorState } from "lexical";
import { Link, useNavigate } from "react-router-dom";

import { EventType, SectionType } from "apollo/graphql.generated";
import { TOOLS_URL, GATES_URL } from "config";
import {
  createThemeForPage,
  mapStateToPageTheme,
} from "styles/theme/themeForPage";
import { useGatesQuery } from "~/features/gates/queries/gates.graphql.generated";
import { AppFooter } from "~/features/layout/Footer";
import { AppHeader } from "~/features/layout/Header";
import { PreviewLocked } from "~/features/pages/ManagePage/PreviewLocked";
import {
  PagesDocument,
  usePublishMutation,
} from "~/features/pages/queries/page.graphql.generated";

import useAnalytics from "../../../hooks/useAnalytics";
import FeatureNotAvailable from "../../layout/FeatureNotAvailable";
import { Background } from "../Background";
import { processEditorState } from "../Editor/logic";

import ContentTab from "./ContentTab";
import PreviewColumnNavigation from "./PreviewColumnNavigation";
import { PreviewUnlocked } from "./PreviewUnlocked";
import SegmentedControl from "./SegmentedControl";
import ThemeTab from "./ThemeTab";
import { editorStateContainsTypes, usePageUpload } from "./logic";
import {
  pageVar,
  addToFilesForUpload,
  resetFilesForUpload,
  filesForUploadVar,
  clearPage,
  setPageName,
  setGate,
  setGateId,
} from "./state";
import * as styles from "./styles";

const previewTabOptions = {
  unlocked: "Unlocked",
  locked: "Locked",
};

const pageTabOptions = {
  content: "Add content",
  theme: "Edit theme",
};

type Props = {
  editMode?: boolean;
  onSave?: () => void;
  isUpdating?: boolean;
};

const ManagePage: FC<Props> = ({ editMode = false, onSave, isUpdating }) => {
  const alert = useAlert();
  usePageVisitedEvent("CREATE_PAGE");
  const disablePageCreate = useFeatureFlags(FEATURE_FLAGS.DISABLE_PAGE_CREATE);
  const showLockedPreviewToggle = useFeatureFlags(
    FEATURE_FLAGS.SHOW_LOCKED_PREVIEW_TOGGLE_IN_TOOLS
  );

  const page = useReactiveVar(pageVar);

  const [view, setView] = useState<string>(pageTabOptions.content);
  const [previewView, setPreviewView] = useState<string | null>(
    previewTabOptions.unlocked
  );
  const [showPlaceholder, setShowPlaceholder] = useState<boolean>(!editMode);
  const [pageUrl, setPageUrl] = useState<string | null>(null);
  const [changedTheme, setChangedTheme] = useState(false);
  const [isChanged, setChanged] = useState(false);
  const [initialEditorContent, setInitialEditorContent] = useState(
    editMode
      ? page.sections[0]?.content.editorContent
        ? page.sections[0]?.content.editorContent.state
        : ""
      : ""
  );
  const [pageNameInput, setPageNameInput] = useState<string>(page.name || "");
  const [selectedGateId, setSelectedGateId] = useState<string>(
    page.gate && page.gate.id ? page.gate.id : ""
  );

  const [published, setPublished] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const { trackEvent } = useAnalytics();

  const [publishMutation, { loading }] = usePublishMutation({
    refetchQueries: [PagesDocument],
  });
  const navigate = useNavigate();

  const { getPageUploadUrlData } = usePageUpload();

  const setChangedTrue = useCallback(() => setChanged(true), [setChanged]);

  useBeforeunload((event) => {
    if (isChanged) {
      event.preventDefault();
    }
  });

  const { data: gatesData, loading: gatesLoading } = useGatesQuery();

  const gate = gatesData?.gates.find((gate) => gate.id === selectedGateId);
  const { data: gateAccessData } = useCheckGateAccessQuery({
    variables: {
      gateId: selectedGateId,
    },
    skip: !selectedGateId,
  });

  // Maps data to simulate a gate with failed check.
  const lockedGateData = {
    ...gateAccessData?.checkGateAccess,
    conditionResults: gateAccessData?.checkGateAccess.conditionResults?.map(
      (condition) => ({
        ...condition,
        passed: false,
      })
    ),
    passed: false,
  };

  const handleSave = useCallback(() => {
    setChanged(false);
    if (onSave) {
      onSave();
    }
  }, [onSave]);

  useEffect(() => {
    resetFilesForUpload();
    // TODO - deduplicate? refresh cause multiple events sent - essentially tracking page views
    // is it better to have it in the web-graphql, get page logic?
    if (editMode) {
      trackEvent(EventType.ExistingGateEditor, {});
    }

    const handlePagePublished = () => {
      setPageNameInput("");
      setInitialEditorContent("");
      setShowPlaceholder(true);
      setView(pageTabOptions.content);
      setChanged(false);
    };

    const eventChangeName = "page:nameChanged";
    const eventPagePublished = "page:published";
    document.addEventListener(eventChangeName, setChangedTrue);
    document.addEventListener(eventPagePublished, handlePagePublished);
    return () => {
      document.removeEventListener(eventChangeName, setChangedTrue);
      document.removeEventListener(eventPagePublished, handlePagePublished);
    };
  }, []);

  const onFileSelected = useCallback((file: File) => {
    addToFilesForUpload(file);
  }, []);

  useEffect(() => {
    switch (view) {
      case pageTabOptions.content:
        setPreviewView(previewTabOptions.unlocked);
        break;
      default:
        break;
    }
  }, [view]);

  useEffect(() => {
    if (page.slug) {
      setPageUrl(`${GATES_URL.page.view.replace(":pageSlug", page.slug)}`);
    } else {
      setPageUrl(null);
    }
  }, [page.slug]);

  useEffect(() => {
    setGate(gate ?? null);
  }, [gate]);

  useEffect(() => {
    setPageName(pageNameInput);
  }, [pageNameInput]);

  // Use layout effect to avoid blinking when changing from gate to no gate.
  useLayoutEffect(() => {
    if (selectedGateId) {
      setGateId(selectedGateId);
    } else {
      setPreviewView(previewTabOptions.unlocked);
    }
  }, [selectedGateId]);

  const getPageTabIcon = (option: string) => {
    if (option === pageTabOptions.content) {
      return <Browser css={styles.pageTabIcon} />;
    }
    if (option === pageTabOptions.theme) {
      return <PencilFill css={styles.pageTabIcon} />;
    }
    return <></>;
  };

  const handlePublishClick = useCallback(async () => {
    try {
      if (published) {
        return;
      }

      setIsUploading(true);

      const filesForUpload = filesForUploadVar();
      const sections = await Promise.all(
        page.sections.map(async (section) => {
          const processedContent = JSON.stringify(
            await processEditorState(
              JSON.parse(
                section.content.editorContent
                  ? section.content.editorContent.state
                  : ""
              ),
              filesForUpload,
              getPageUploadUrlData
            )
          );

          return {
            id: "main",
            content: {
              editorContent: {
                state: processedContent,
              },
            },
            type: SectionType.EditorData,
          };
        })
      );
      setIsUploading(false);

      await publishMutation({
        variables: {
          input: {
            name: page?.name?.trim() || "",
            sections,
            theme: mapStateToPageTheme(page.theme),
            gateId: page.gate?.id || undefined,
          },
        },
      });
      clearPage();
      setPublished(true);

      alert.success("Page created!");
      document.dispatchEvent(new CustomEvent<string>("page:published"));

      const contentTypes = await editorStateContainsTypes(page.sections);
      const eventData = {
        isGated: !!page.gateId,
        changedTheme,
        ...contentTypes,
      };
      trackEvent(EventType.CreatedGate, eventData);
    } catch (e: unknown) {
      await logError({
        e,
        message: "[handlePublishClick] page publish failed",
      });
      alert.error("Error creating page. Please try again later.");
    } finally {
      setIsUploading(false);
    }
  }, [published, page]);

  const handlePublishOnClick = useCallback(() => {
    if (!published) {
      trackEvent(EventType.CreateGateEditor_PublishClick, {});
      handlePublishClick();
    }
  }, [showPlaceholder, page]);

  useEffect(() => {
    if (published) {
      navigate(TOOLS_URL.pages.list);
      return;
    }
  }, [published]);

  useEffect(() => {
    setInitialEditorContent(
      page.sections[0]?.content.editorContent
        ? page.sections[0]?.content.editorContent.state
        : ""
    );
  }, [page]);

  const theme = mapStateToPageTheme(page.theme);

  const pageTheme = useMemo(() => createThemeForPage(theme), [theme]);

  const handleEditorChange = useCallback(
    (editorState: EditorState) => {
      editorState.read(() => {
        setShowPlaceholder($canShowPlaceholder(false, true));
      });
      setChanged(true);
    },
    [setShowPlaceholder, setChanged]
  );

  usePrompt("You have unsaved changes. Discard?", isChanged, () => clearPage());

  if (gatesLoading) {
    return <LoadingPage />;
  }

  if (disablePageCreate) {
    return <FeatureNotAvailable />;
  }

  const PublishButton = () => {
    return (
      <Stack mt="md">
        {!editMode ? (
          <Button
            fullWidth
            size="xl"
            loading={loading || published || isUploading}
            onClick={handlePublishOnClick}
            disabled={showPlaceholder || !page.name}
          >
            Publish
          </Button>
        ) : (
          <Button fullWidth onClick={handleSave} loading={isUpdating}>
            Save Changes
          </Button>
        )}
      </Stack>
    );
  };

  /**
   * if user didn't connect account => show the connect-wallet-button
   * do this to avoid styling issue -> react-modal doesn't use the <RainbowkitProvider /> context
   * simplest solution; alternatives are:
   * (a) create custom implementation of RK button
   * (b) copy-paste a ton of redundant css for modal <ConnectAccount /> button
   */
  return (
    <>
      <AppSEO title={page.name || "New page"} />
      <AppShell
        padding="md"
        header={
          <AppHeader
            leftButtons={
              <Link to={TOOLS_URL.pages.list}>
                <Button
                  color={SECONDARY_COLOR}
                  leftIcon={<ArrowLeftIcon height={12} />}
                >
                  Back
                </Button>
              </Link>
            }
          />
        }
        footer={<AppFooter />}
      >
        <Container size="md">
          <Grid mt={64} gutter={40}>
            <Grid.Col span={7}>
              <SegmentedControl
                options={Object.values(pageTabOptions)}
                getIcon={getPageTabIcon}
                selectedOption={view}
                setOption={setView}
              />
              <div css={styles.mainContainer}>
                {view === pageTabOptions.content && (
                  <ContentTab
                    content={initialEditorContent}
                    gates={gatesData?.gates || []}
                    gateId={selectedGateId}
                    selectedGateIdOnChange={setSelectedGateId}
                    pageName={pageNameInput}
                    pageNameOnChange={setPageNameInput}
                    onEditorChange={handleEditorChange}
                    onFileSelected={onFileSelected}
                  />
                )}
                {view === pageTabOptions.theme && (
                  <ThemeTab
                    page={page}
                    onChange={() => {
                      setChangedTrue();
                      setChangedTheme(true);
                    }}
                  />
                )}
                <PublishButton />
              </div>
            </Grid.Col>
            <Grid.Col span={5}>
              <Group position="apart">
                <Text weight={500} size="sm">
                  Page preview
                </Text>
                {editMode && pageUrl && (
                  <PreviewColumnNavigation
                    pageUrl={pageUrl}
                    pageSlug={page.slug ?? ""}
                    editMode={editMode}
                  />
                )}
              </Group>
              <Card
                mt={16}
                p={0}
                withBorder
                h="100%"
                radius="md"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: "1 1 auto",
                  minHeight: "400px",
                  maxHeight:
                    previewView === previewTabOptions.unlocked &&
                    showPlaceholder
                      ? "720px"
                      : "unset",
                  overflow: "hidden",
                }}
              >
                <Tabs
                  value={previewView}
                  onTabChange={setPreviewView}
                  display="flex"
                  styles={{
                    root: {
                      flexDirection: "column",
                      flex: "1",
                    },
                  }}
                >
                  <Tabs.List
                    grow
                    position="apart"
                    hidden={!showLockedPreviewToggle}
                  >
                    <Tabs.Tab
                      icon={<LockFill css={styles.previewIcon} />}
                      value={previewTabOptions.locked}
                    >
                      Locked
                    </Tabs.Tab>
                    <Tabs.Tab
                      icon={<LockOpenFill css={styles.previewIcon} />}
                      value={previewTabOptions.unlocked}
                    >
                      Unlocked
                    </Tabs.Tab>
                  </Tabs.List>

                  <ThemeProvider theme={pageTheme}>
                    <Tabs.Panel
                      display="flex"
                      sx={(theme) => ({
                        flex: "1",
                        backgroundColor: theme.colors.baseBackground[0],
                      })}
                      value={previewTabOptions.unlocked}
                    >
                      <Background hide={showPlaceholder}>
                        <PreviewUnlocked
                          content={initialEditorContent}
                          showPlaceHolder={showPlaceholder}
                        />
                      </Background>
                    </Tabs.Panel>
                    <Tabs.Panel
                      display="flex"
                      sx={{ flex: "1" }}
                      value={previewTabOptions.locked}
                    >
                      <Background>
                        <PreviewLocked
                          page={page}
                          unlockGateResult={lockedGateData}
                        />
                      </Background>
                    </Tabs.Panel>
                  </ThemeProvider>
                </Tabs>
              </Card>
            </Grid.Col>
          </Grid>
        </Container>
      </AppShell>
    </>
  );
};

export default ManagePage;
